import { useLazyQuery } from "@apollo/client";
import { isArray } from "@apollo/client/cache/inmemory/helpers";
import { Q } from "@nozbe/watermelondb";
import { useDatabase } from "@nozbe/watermelondb/hooks";
import { useWithAppState } from "@towersystems/roam-common-native";
import { useFlashMessages } from "@towersystems/roam-common-native/lib/utilities/flash-messages";
import {
  Layout as LayoutEntity,
  LayoutNodeFilterType,
} from "@towersystems/roam-common/lib/generated-types";
import { capitalize } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import Layout from "../../../../utilities/data/model/Layout";
import {
  deriveLayoutDimensions,
  useLayoutPages,
} from "../../../../utilities/layout";
import { QUERY_LAYOUT_BY_TERMINAL_ID } from "../../../../utilities/layout/constants";
import { createLayoutDefinition } from "../../../../utilities/layout/definitions/create-layout-definition";
import { useLocalSettingsProvider } from "../../../../utilities/local-settings";
import { LayoutContext } from "../../utils/layout";
import { useRegistryMaintainer } from "./hooks";
import { EnhancedLayoutReturn, LayoutProviderProps } from "./types";
import { enhanceLayout } from "./utils";

function LayoutProviderToEnhance({
  editLayout,
  layouts,
  layoutNodes = [],
  products = [],
  categories = [],
  departments = [],
  refetchLayout,
  children,
}: EnhancedLayoutReturn) {
  const database = useDatabase();
  const listRef = useRef<any | null>(null);
  const { settings } = useLocalSettingsProvider();
  const [width, setWidth] = useState<number | undefined>(undefined);
  const { showMessage } = useFlashMessages();
  const [queryLayoutByTerminalId] = useLazyQuery<{
    layoutByTerminalId: LayoutEntity;
  }>(QUERY_LAYOUT_BY_TERMINAL_ID);

  const { getById: getProductById } = useRegistryMaintainer(products);
  const { getById: getCategoryById } = useRegistryMaintainer(categories);
  const { getById: getDepartmentById } = useRegistryMaintainer(departments);

  const {
    appState: { terminalId },
    updateAppState,
  } = useWithAppState();

  const [isEditing, setIsEditing] = useState(!!editLayout);

  const layout = isArray(layouts) ? layouts[0] : layouts;

  const layoutPagesConfig = {
    isEditing,
    layoutNodes,
    width,
    onPageChanged,
  };

  const layoutDimensions = useMemo(
    () => (width ? deriveLayoutDimensions(width) : undefined),
    [width]
  );

  function onPageChanged(page: number) {
    if (!layoutDimensions) return;

    listRef.current?.scrollToOffset({
      animated: true,
      offset: (page - 1) * layoutDimensions?.width,
    });
  }

  const pagesController = useLayoutPages(layoutPagesConfig);

  function handleStopEditing() {
    setIsEditing(false);
  }

  async function findOrCreateLayout(terminalId: string) {
    // find layout by terminal id
    const queryResult = await queryLayoutByTerminalId({
      variables: {
        id: terminalId,
      },
    }).catch(console.log);

    const foundLayout = queryResult?.data?.layoutByTerminalId;

    if (foundLayout) {
      return foundLayout;
    }

    return createLayoutDefinition.config.remoteAction({
      terminalId,
    });
  }

  async function createAndSyncLayout(terminalId: string) {
    const remoteLayout = await findOrCreateLayout(terminalId);

    const localLayout = await createLayoutDefinition.config.localAction(
      database,
      remoteLayout,
      remoteLayout.nodes
    );

    return localLayout;
  }

  function handleStartEditing() {
    if (!settings.allowLayoutEdit) return;
    // layout is null when there is no layout for active terminal
    const layoutDoesNotExist = layout === null && terminalId;

    if (layoutDoesNotExist) {
      createAndSyncLayout(terminalId)
        .then((layout) => {
          updateAppState({ layoutId: layout.id });
          setIsEditing(true);
          refetchLayout();
        })
        .catch((e) => {
          showMessage({
            message: `Failed to create layout: ${e?.toString()}`,
            severity: "error",
          });
        });
    } else {
      setIsEditing(true);
    }
  }

  function getNameFromFilter(filterType: LayoutNodeFilterType, id: string) {
    switch (filterType) {
      case LayoutNodeFilterType.CATEGORY:
        return getCategoryById(id)?.name;
      case LayoutNodeFilterType.DEPARTMENT:
        return getDepartmentById(id)?.name;
      default:
        return capitalize(filterType);
    }
  }

  const value = {
    layout,
    layoutNodes,
    isEditing,
    listRef,
    pagesController,
    width,
    layoutDimensions,
    getProductById,
    getCategoryById,
    getDepartmentById,
    getNameFromFilter,
    setWidth,
    startEditing: handleStartEditing,
    stopEditing: handleStopEditing,
  };

  return (
    <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>
  );
}

const EnhancedLayoutProvider = enhanceLayout(LayoutProviderToEnhance);

export const LayoutProvider = (props: LayoutProviderProps) => {
  const { appState, updateAppState } = useWithAppState();
  const [layoutId, setLayoutId] = useState<string | undefined>(undefined);
  const { terminalId } = appState;

  const database = useDatabase();

  function refetchLayout() {
    if (!terminalId) return;

    database
      .get<Layout>("layouts")
      .query(Q.where("terminal_id", Q.eq(terminalId)))
      .fetch()
      .then((layouts) => {
        const layout = layouts[0];
        const newLayoutId = layout.id || undefined;
        setLayoutId(newLayoutId);
        ///updateAppState({ layoutId: newLayoutId });
      })
      .catch(() => {
        setLayoutId(undefined);
      });
  }

  useEffect(() => {
    if (!terminalId) return;
    refetchLayout();
  }, [terminalId]);

  return (
    <EnhancedLayoutProvider
      layoutId={layoutId}
      database={database}
      terminalId={terminalId}
      refetchLayout={refetchLayout}
      {...props}
    />
  );
};
